import { ProjectsColumns } from "./columns";
import { z } from "zod";
import { AdjudicatedTermsSchema } from "../schemas/terms";
import { ClientType } from "./clients";

export const PROJECT_STATUSES = [
  "POTENTIAL",
  "PENDING APPROVAL",
  "IN PROGRESS",
  "ON HOLD",
  "COMPLETED",
  "CONFLICTING",
  "LOST",
  "CANCELED",
] as const;

export const ProjectStatusSchema = z.enum(PROJECT_STATUSES).default("POTENTIAL");

export type ProjectStatusType = z.infer<typeof ProjectStatusSchema>;

export const PROJECT_BILLING_STATUSES = [
  "PENDING",
  "READY FOR BILLING",
  "BILLING IN PROGRESS",
  "PNL PENDING",
  "COMPLETED",
] as const;

export const ProjectBillingStatusSchema = z.enum(PROJECT_BILLING_STATUSES).optional();

export type ProjectBillingStatusType = z.infer<typeof ProjectBillingStatusSchema>;

export const PROJECT_VENUE_TYPES = ["Indoor", "Outdoor"] as const;
export const ProjectVenueTypeSchema = z.enum(PROJECT_VENUE_TYPES).optional();
export type ProjectVenueType = z.infer<typeof ProjectVenueTypeSchema>;

export const NoteSchema = z.object({
  id: z.string().uuid(),
  author_id: z.string().uuid(),
  author_name: z.string(),
  message: z.string(),
  created_at: z.string(),
  position_ids: z.array(z.string().uuid()).optional(),
});

export type NoteType = z.infer<typeof NoteSchema>;

export const ClockInOptionsSchema = z.object({
  app: z.boolean().optional(),
  qr_code: z.boolean().optional(),
  timesheet: z.boolean().optional(),
});

export type ClockInOptionsType = z.infer<typeof ClockInOptionsSchema>;

export const ProjectMetaSchema = z.object({
  terms: AdjudicatedTermsSchema,
  dress_code_id: z.string().uuid().optional(),
  venue_type: ProjectVenueTypeSchema,
  notes: z.array(NoteSchema).optional(),
  markets: z.array(z.string().uuid()).optional(),
  clock_in_options: ClockInOptionsSchema.optional(),
  billing_status: ProjectBillingStatusSchema,
  deposit_amount: z.number().nullable().optional(),
});

export type ProjectMetaType = z.infer<typeof ProjectMetaSchema>;

export type ProjectType = Omit<ProjectsColumns, "meta_data" | "status"> & {
  status: ProjectStatusType;
  client?: Partial<ClientType>;
  meta_data: ProjectMetaType;
  start_time?: any;
  end_time?: any;
  // Shifts joined from public.shifts (for computed dates)
  shifts?: {
    start_time: string;
    end_time: string;
  }[];
  // Enriched location from lists.locations
  location?: {
    id: string;
    name: string;
    address?: {
      street?: string;
      city?: string;
      state?: string;
      zip?: string;
    };
  };
  // Enriched dress code from lists.dress_codes (stored as meta_data.dress_code_id)
  dress_code?: {
    id: string;
    name: string;
    description?: string;
  };
  // Link table relationships
  project_abilities?: {
    ability: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
  project_restrictions?: {
    restriction: {
      id: string;
      name: string;
      description?: string;
    };
  }[];
};
